import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { pedidos } from '@/utils/pedidos'
import useQuerySync from '@/utils/useQuerySync'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function usePedidosList() {
  // Use toast
  const toast = useToast()

  const refPedidosListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'is_urgent', label: 'Prioridad', sortable: true },
    { key: 'codigo', label: 'Código', sortable: true },
    { key: 'proveedor_name', label: 'Proveedor', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true },
    { key: 'fecha_enviado', label: 'Fecha envío', sortable: true },
    { key: 'fk_user_creado', label: 'Creado por', sortable: true },
    { key: 'estado', label: 'Estado', sortable: true },
    { key: 'actions', label: 'Acciones', thStyle: { minWidth: '150px' } },
  ]
  const perPage = ref(50)
  const totalPedidos = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const statusFilter = ref(null)
  const proveedorFilter = ref(null)
  const isFiltering = ref(false)

  useQuerySync({
    search: searchQuery,
    status: statusFilter,
    provider: { ref: proveedorFilter, type: 'number' },
    page: currentPage,
    per_page: perPage,
  })

  const dataMeta = computed(() => {
    const localItemsCount = refPedidosListTable.value ? refPedidosListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPedidos.value,
    }
  })

  const refetchData = () => {
    refPedidosListTable.value.refresh()
  }

  const filterData = () => {
    currentPage.value = 1
    refetchData()
  }

  const clearFilter = () => {
    isFiltering.value = false
    proveedorFilter.value = null
    statusFilter.value = null
    searchQuery.value = ''
    refetchData()
  }

  watch([currentPage, perPage, searchQuery, statusFilter, proveedorFilter], () => {
    refetchData()
  })

  const fetchPedidos = (ctx, callback) => {
    store
      .dispatch('app-pedidos-proveedores/fetchPedidosProveedores', {
        body: {
          search: searchQuery.value,
          status: statusFilter.value,
          paginate: perPage.value,
          user_created: store.state.auth.user.fk_role !== 1 ? store.state.auth.user.id : null,
          proveedor: proveedorFilter?.value ?? null,
        },
        headers: {
          'X-Sort': `pedidos_proveedores.${sortBy.value}`,
          'X-Sort-Order': isSortDirDesc.value ? 'desc' : 'asc',
        },
        query: {
          page: currentPage.value,
        },
      })
      .then(res => {
        const { total } = res
        totalPedidos.value = total
        callback(res.data)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching pedidos list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const parseDate = date => {
    let d = date.substring(0, 10)
    d = d.split('-').reverse().join('-')
    const t = date.substring(12, 19)
    return `${d} ${t}`
  }

  const resolveStatusVariant = status => {
    const item = pedidos.estados.find(el => el.value === status)
    if (item) return item.variant
    return '-'
  }

  const resolveStatusText = status => {
    const item = pedidos.estados.find(el => el.value === status)
    if (item) return item.label
    return '-'
  }

  return {
    fetchPedidos,
    tableColumns,
    perPage,
    currentPage,
    totalPedidos,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPedidosListTable,
    proveedorFilter,
    isFiltering,
    filterData,
    clearFilter,

    refetchData,
    parseDate,
    resolveStatusVariant,
    resolveStatusText,

    // Extra Filters
    statusFilter,
  }
}
